/**
 * Search home — the saved-search library: every saved cohort search with its
 * schedule, last-run counts, and "new since last run" chip; entry point to the
 * builder. (docs/crm-search-builder-plan.md §7.2)
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, CalendarClock, Loader2, Play, SearchCheck, Copy, Pencil, Sparkles } from 'lucide-react';
import api from '../../../services/api';
import { PageHeader, RefreshButton } from '../../components';
import { FilterChip } from '../../components/filters';
import ScheduleModal from '../../components/search/ScheduleModal';
import type { RecentSearchRun, SavedSearch, StarterSearch } from '../../types';
import { relTime, useTeam } from '../../lib';
import { Alert } from '../../components/ui';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

/** Row icon-buttons (Edit / Duplicate / Archive) in the saved-search table. */
const rowIconBtnCls = 'rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200';

export default function SearchHome() {
  const navigate = useNavigate();
  const { team } = useTeam();
  const [searches, setSearches] = useState<SavedSearch[]>([]);
  const [recentRuns, setRecentRuns] = useState<RecentSearchRun[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [scheduling, setScheduling] = useState<SavedSearch | null>(null);
  const [starters, setStarters] = useState<StarterSearch[]>([]);

  // Curated seeds (P4) — decorative strip; a fetch failure just hides it.
  useEffect(() => {
    api.get('/api/crm/search/starters')
      .then((r) => setStarters(r.data?.starters || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async (archived: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/crm/searches', { params: archived ? { status: 'archived' } : {} });
      setSearches(res.data?.searches || []);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
    // The strip is decorative — a failure just hides it.
    api.get('/api/crm/search-runs', { params: { limit: 8 } })
      .then((r) => setRecentRuns(r.data?.runs || []))
      .catch(() => {});
  }, []);

  useEffect(() => { load(showArchived); }, [load, showArchived]);

  const runNow = async (s: SavedSearch) => {
    if (runningId) return;
    setRunningId(s.id);
    try {
      const res = await api.post(`/api/crm/searches/${s.id}/run`);
      navigate(`/crm/search/runs/${res.data.run_id}`);
    } catch (e: any) {
      setError(errMsg(e));
      setRunningId(null);
    }
  };

  const archive = async (s: SavedSearch) => {
    try {
      await api.patch(`/api/crm/searches/${s.id}`, { status: s.status === 'archived' ? 'active' : 'archived' });
      load(showArchived);
    } catch (e: any) {
      setError(errMsg(e));
    }
  };

  const duplicate = (s: SavedSearch) =>
    navigate('/crm/search/new', { state: { seed: s.definition, name: `${s.name} (copy)` } });

  const openStarter = (s: StarterSearch) =>
    navigate('/crm/search/new', { state: { seed: s.definition, name: s.name } });

  const areaChipCls: Record<string, string> = {
    Metabolic: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
    Cardiovascular: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
    Operational: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  };

  // Starter cards (P4) — open the builder pre-seeded; nothing saves until the
  // user does. Rendered in the empty state AND as a strip under the table.
  const startersBlock = starters.length > 0 && (
    <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <h2 className="flex items-center gap-1.5 border-b border-gray-100 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
        <Sparkles className="h-3.5 w-3.5" />
        Starter searches
      </h2>
      <div className="grid gap-2 p-3 sm:grid-cols-2 xl:grid-cols-3">
        {starters.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => openStarter(s)}
            className="rounded-lg border border-gray-200 p-2.5 text-left transition-colors duration-100 hover:border-primary-300 hover:bg-primary-50/40 dark:border-gray-700 dark:hover:border-primary-700 dark:hover:bg-primary-900/10"
          >
            <span className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{s.name}</span>
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${areaChipCls[s.area] || areaChipCls.Operational}`}>{s.area}</span>
            </span>
            <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">{s.description}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const thCls = 'px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400';

  return (
    <div className="space-y-4">
      <PageHeader
        icon={SearchCheck}
        title="Search"
        count={searches.length || null}
        subtitle="Build funnel searches over the whole lead database — every level shows who's in, who's out, and who has no data."
        actions={
          <>
            <button
              onClick={() => navigate('/crm/search/new')}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
            >
              <SearchCheck className="h-4 w-4" />
              New search
            </button>
            <FilterChip active={showArchived} onClick={() => setShowArchived((v) => !v)} icon={<Archive className="h-4 w-4" />}>
              Archived
            </FilterChip>
            <RefreshButton onClick={() => load(showArchived)} loading={loading} label="Refresh searches" />
          </>
        }
      />

      {error && (
        <Alert tone="error" icon={false}>{error}</Alert>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : searches.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center dark:border-gray-600 dark:bg-gray-800">
          <SearchCheck className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600" />
          <h2 className="mt-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
            {showArchived ? 'No archived searches' : 'Build your first cohort search'}
          </h2>
          {!showArchived && (
            <>
              <p className="mx-auto mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
                Stack criteria — conditions, medications, labs, form answers, sources — into a funnel.
                Each step reports how many leads it keeps, drops, and has no data for; the result is a
                saved list you can rerun and work from the board.
              </p>
              <button
                onClick={() => navigate('/crm/search/new')}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
              >
                New search
              </button>
              {startersBlock && <div className="mt-4 text-left">{startersBlock}</div>}
            </>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className={thCls}>Search</th>
                <th className={thCls}>Schedule</th>
                <th className={thCls}>Last run</th>
                <th className={thCls}>List</th>
                <th className={`${thCls} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {searches.map((s) => (
                <tr key={s.id} className="transition-colors duration-100 hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className="px-3 py-2.5">
                    <button onClick={() => navigate(`/crm/search/${s.id}`)} className="text-left">
                      <span className="block text-sm font-medium text-gray-800 hover:text-primary-700 dark:text-gray-100 dark:hover:text-primary-400">{s.name}</span>
                      {s.description && <span className="block max-w-md truncate text-xs text-gray-400">{s.description}</span>}
                    </button>
                  </td>
                  <td className="px-3 py-2.5">
                    <button
                      type="button"
                      onClick={() => setScheduling(s)}
                      disabled={s.status === 'archived'}
                      title="Edit schedule + notify recipients"
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                        s.schedule_enabled
                          ? 'bg-primary-50 text-primary-700 hover:bg-primary-100 dark:bg-primary-900/20 dark:text-primary-400 dark:hover:bg-primary-900/40'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      <CalendarClock className="h-3 w-3" />
                      {s.schedule_summary || 'Manual'}
                      {(s.notify?.user_ids?.length || 0) > 0 && (
                        <span title={`Emails ${s.notify!.user_ids.length} recipient(s) after scheduled runs`}>
                          · ✉{s.notify!.user_ids.length}
                        </span>
                      )}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-300">
                    {s.last_run ? (
                      <button onClick={() => navigate(`/crm/search/runs/${s.last_run!.id}`)} className="text-left hover:underline">
                        {s.last_run.status === 'complete'
                          ? <>{relTime(s.last_run.finished_at)}</>
                          : <span className="text-amber-600 dark:text-amber-400">{s.last_run.status}</span>}
                      </button>
                    ) : <span className="text-gray-400">never</span>}
                  </td>
                  <td className="px-3 py-2.5 text-sm tabular-nums text-gray-700 dark:text-gray-200">
                    {s.last_run?.final_total != null ? (
                      <>
                        {s.last_run.final_total.toLocaleString()}
                        {(s.last_run.new_matches || 0) > 0 && (
                          <span className="ml-1.5 rounded-full bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-300">
                            {s.last_run!.new_matches} new
                          </span>
                        )}
                      </>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => runNow(s)}
                        disabled={runningId !== null || s.status === 'archived'}
                        title="Run now"
                        className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-primary-50 hover:text-primary-700 disabled:opacity-40 dark:hover:bg-primary-900/20 dark:hover:text-primary-400"
                      >
                        {runningId === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                      </button>
                      <button onClick={() => navigate(`/crm/search/${s.id}`)} title="Edit" className={rowIconBtnCls}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => duplicate(s)} title="Duplicate" className={rowIconBtnCls}>
                        <Copy className="h-4 w-4" />
                      </button>
                      <button onClick={() => archive(s)} title={s.status === 'archived' ? 'Restore' : 'Archive'} className={rowIconBtnCls}>
                        <Archive className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && searches.length > 0 && !showArchived && startersBlock}

      {recentRuns.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <h2 className="border-b border-gray-100 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
            Recent runs
          </h2>
          <ul className="divide-y divide-gray-50 dark:divide-gray-700/50">
            {recentRuns.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/crm/search/runs/${r.id}`)}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors duration-100 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                >
                  <span className="min-w-0 flex-1 truncate text-sm text-gray-800 dark:text-gray-100">
                    {r.search_name || 'Ad-hoc search'}
                    <span className="ml-1.5 text-xs text-gray-400">{r.trigger === 'schedule' ? 'scheduled' : 'manual'}</span>
                  </span>
                  {r.status === 'complete' ? (
                    <span className="text-sm tabular-nums text-gray-700 dark:text-gray-200">
                      {r.final_total?.toLocaleString() ?? '—'}
                      {(r.new_matches || 0) > 0 && (
                        <span className="ml-1.5 rounded-full bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-300">
                          {r.new_matches} new
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className={`text-xs font-medium ${r.status === 'failed' ? 'text-rose-600 dark:text-rose-400' : 'text-amber-600 dark:text-amber-400'}`}>
                      {r.status}
                    </span>
                  )}
                  <span className="hidden text-xs text-gray-400 sm:inline">{relTime(r.created_at)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {scheduling && (
        <ScheduleModal
          search={scheduling}
          team={team}
          onSaved={() => load(showArchived)}
          onClose={() => setScheduling(null)}
        />
      )}
    </div>
  );
}
